import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Receipt {
  id: string;
  receiptNumber: string;
  orderNumber: string;
  customer: string;
  total: number;
  paymentMethod: string;
  type: "sale" | "refund" | "exchange";
  date: string;
  time: string;
  cashier: string;
  terminal: string;
  printed: boolean;
  emailed: boolean;
}

const MOCK_RECEIPTS: Receipt[] = [
  { id: "1", receiptNumber: "RCP-2024-001", orderNumber: "ORD-2024-001", customer: "John Doe", total: 125.99, paymentMethod: "Card", type: "sale", date: "2024-01-15", time: "10:30 AM", cashier: "Maria G.", terminal: "Terminal 1", printed: true, emailed: true },
  { id: "2", receiptNumber: "RCP-2024-002", orderNumber: "ORD-2024-002", customer: "Jane Smith", total: 289.50, paymentMethod: "Cash", type: "sale", date: "2024-01-15", time: "11:15 AM", cashier: "Carlos R.", terminal: "Terminal 2", printed: true, emailed: false },
  { id: "3", receiptNumber: "RCP-2024-003", orderNumber: "REF-2024-001", customer: "Mike Wilson", total: -45.99, paymentMethod: "Card", type: "refund", date: "2024-01-15", time: "12:00 PM", cashier: "Maria G.", terminal: "Terminal 1", printed: true, emailed: true },
  { id: "4", receiptNumber: "RCP-2024-004", orderNumber: "ORD-2024-004", customer: "Sarah Johnson", total: 89.99, paymentMethod: "Card", type: "sale", date: "2024-01-15", time: "02:30 PM", cashier: "Ana L.", terminal: "Terminal 3", printed: false, emailed: true },
  { id: "5", receiptNumber: "RCP-2024-005", orderNumber: "EXC-2024-001", customer: "Alex Brown", total: 0.00, paymentMethod: "N/A", type: "exchange", date: "2024-01-14", time: "04:45 PM", cashier: "Maria G.", terminal: "Terminal 1", printed: true, emailed: false },
  { id: "6", receiptNumber: "RCP-2024-006", orderNumber: "ORD-2024-006", customer: "Emily Davis", total: 67.00, paymentMethod: "Card", type: "sale", date: "2024-01-14", time: "05:20 PM", cashier: "Carlos R.", terminal: "Terminal 2", printed: true, emailed: true },
];

export default function ReceiptsSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredReceipts = MOCK_RECEIPTS.filter((receipt) => {
    const matchesSearch = receipt.receiptNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      receipt.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      receipt.customer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "all" || receipt.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const getTypeBadge = (type: Receipt["type"]) => {
    const styles = {
      sale: "bg-emerald-500/20 text-emerald-500",
      refund: "bg-red-500/20 text-red-500",
      exchange: "bg-blue-500/20 text-blue-500",
    };
    const labels = {
      sale: t("receipts.typeSale"),
      refund: t("receipts.typeRefund"),
      exchange: t("receipts.typeExchange"),
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[type]}`}>
        {labels[type]}
      </span>
    );
  };

  const todayReceipts = MOCK_RECEIPTS.filter((r) => r.date === "2024-01-15").length;
  const printedReceipts = MOCK_RECEIPTS.filter((r) => r.printed).length;
  const emailedReceipts = MOCK_RECEIPTS.filter((r) => r.emailed).length;
  const totalSales = MOCK_RECEIPTS.filter((r) => r.type === "sale").reduce((acc, r) => acc + r.total, 0);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posMenu.receiptHistory")}</h1>
          <p className={themeClasses.textSecondary}>{t("receipts.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-download-2-line"></i>
            {t("posOrders.export")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("receipts.todayReceipts")}</span>
            <i className="ri-receipt-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{todayReceipts}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("receipts.printed")}</span>
            <i className="ri-printer-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-emerald-500`}>{printedReceipts}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("receipts.emailed")}</span>
            <i className="ri-mail-line text-violet-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-violet-500`}>{emailedReceipts}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("receipts.totalSales")}</span>
            <i className="ri-money-dollar-circle-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-emerald-500`}>${totalSales.toFixed(2)}</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("receipts.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <div className="flex gap-2">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
            >
              <option value="all">{t("receipts.allTypes")}</option>
              <option value="sale">{t("receipts.typeSale")}</option>
              <option value="refund">{t("receipts.typeRefund")}</option>
              <option value="exchange">{t("receipts.typeExchange")}</option>
            </select>

            <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} flex items-center gap-2`}>
              <i className="ri-calendar-line"></i>
              {t("posOrders.dateRange")}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("receipts.receiptNumber")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("receipts.orderNumber")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.customer")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("receipts.type")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.total")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.cashier")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("receipts.delivery")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("posOrders.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredReceipts.map((receipt) => (
                <tr key={receipt.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                  <td className="px-4 py-3">
                    <div>
                      <p className={`font-medium ${themeClasses.text}`}>{receipt.receiptNumber}</p>
                      <p className={`text-xs ${themeClasses.textSecondary}`}>{receipt.date} {receipt.time}</p>
                    </div>
                  </td>
                  <td className={`px-4 py-3 ${themeClasses.text}`}>
                    <span className="text-blue-500 hover:underline cursor-pointer">{receipt.orderNumber}</span>
                  </td>
                  <td className={`px-4 py-3 ${themeClasses.text}`}>{receipt.customer}</td>
                  <td className="px-4 py-3 text-center">{getTypeBadge(receipt.type)}</td>
                  <td className={`px-4 py-3 text-right font-bold ${receipt.total < 0 ? "text-red-500" : themeClasses.text}`}>
                    ${Math.abs(receipt.total).toFixed(2)}
                  </td>
                  <td className={`px-4 py-3 text-center ${themeClasses.textSecondary}`}>{receipt.cashier}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-2">
                      <i className={`ri-printer-line ${receipt.printed ? "text-emerald-500" : "text-gray-500"}`}></i>
                      <i className={`ri-mail-line ${receipt.emailed ? "text-violet-500" : "text-gray-500"}`}></i>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button aria-label={t("general.viewDetails", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-eye-line"></i>
                      </button>
                      <button aria-label={t("general.print", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-printer-line"></i>
                      </button>
                      <button className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-mail-send-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
